import { createSelector } from '@reduxjs/toolkit';
import { RootState } from '../store';
import { UserFavoritesResponse } from '../../types/userPreferences/userPreferences.types';

// Base selectors for user preferences
export const selectUserPreferencesState = (state: RootState) => state.userPreferences;

export const selectPreferences = (state: RootState) => state.userPreferences.preferences;

export const selectFavorites = (state: RootState): UserFavoritesResponse | null => state.userPreferences.favorites;

export const selectIsTogglingFavorite = (state: RootState) => state.userPreferences.isTogglingFavorite;

export const selectToggleFavoriteError = (state: RootState) => state.userPreferences.toggleFavoriteError;

export const selectIsLoadingPreferences = (state: RootState) => state.userPreferences.isLoadingPreferences;

export const selectIsLoadingFavorites = (state: RootState) => state.userPreferences.isLoadingFavorites;

export const selectPreferencesError = (state: RootState) => state.userPreferences.preferencesError;

export const selectFavoritesError = (state: RootState) => state.userPreferences.favoritesError;

export const selectHasAttemptedPreferencesFetch = (state: RootState) =>
  state.userPreferences.hasAttemptedPreferencesFetch;

export const selectHasAttemptedFavoritesFetch = (state: RootState) => state.userPreferences.hasAttemptedFavoritesFetch;

// Derived selector to check if any preferences are loading
export const selectIsLoadingAnyPreferences = createSelector(
  [selectIsLoadingPreferences, selectIsLoadingFavorites],
  (isLoadingPreferences, isLoadingFavorites) => {
    return isLoadingPreferences || isLoadingFavorites;
  }
);

// Derived selector to check if there are any errors
export const selectHasAnyErrors = createSelector(
  [selectPreferencesError, selectFavoritesError],
  (preferencesError, favoritesError) => {
    return preferencesError !== null || favoritesError !== null;
  }
);

// Derived selector to get all errors as an array
export const selectAllErrors = createSelector(
  [selectPreferencesError, selectFavoritesError],
  (preferencesError, favoritesError) => {
    const errors: string[] = [];
    if (preferencesError) errors.push(preferencesError);
    if (favoritesError) errors.push(favoritesError);
    return errors;
  }
);

// Derived selector to check if both preferences and favorites have been fetched
export const selectHasFetchedAllData = createSelector(
  [selectHasAttemptedPreferencesFetch, selectHasAttemptedFavoritesFetch],
  (hasAttemptedPreferencesFetch, hasAttemptedFavoritesFetch) => {
    return hasAttemptedPreferencesFetch && hasAttemptedFavoritesFetch;
  }
);

// Derived selector to check if preferences are ready (fetched and not loading)
export const selectArePreferencesReady = createSelector(
  [selectHasAttemptedPreferencesFetch, selectIsLoadingPreferences, selectPreferences],
  (hasAttempted, isLoading, preferences) => {
    return hasAttempted && !isLoading && preferences !== null;
  }
);

// Derived selector to check if favorites are ready (fetched and not loading)
export const selectAreFavoritesReady = createSelector(
  [selectHasAttemptedFavoritesFetch, selectIsLoadingFavorites, selectFavorites],
  (hasAttempted, isLoading, favorites) => {
    return hasAttempted && !isLoading && favorites !== null;
  }
);

// Derived selector to get product line favorite IDs
export const selectProductLineFavoriteIds = createSelector([selectFavorites], (favorites) => {
  return favorites?.product_line_ids ?? [];
});

// Derived selector to get RPN favorite IDs
export const selectRpnFavoriteIds = createSelector([selectFavorites], (favorites) => {
  return favorites?.rpn_ids ?? [];
});

// Derived selector to get FPN favorite IDs
export const selectFpnFavoriteIds = createSelector([selectFavorites], (favorites) => {
  return favorites?.fpn_ids ?? [];
});

/**
 * Create a Set of all favorite unique IDs for quick lookup
 * Format: "{type}_{id}" e.g., "product_line_281", "rpn_10150", "fpn_4553"
 */
export const selectFavoritesSet = createSelector([selectFavorites], (favorites): Set<string> => {
  const favoritesSet = new Set<string>();

  if (!favorites) return favoritesSet;

  favorites.product_line_ids.forEach((id) => {
    favoritesSet.add(`product_line_${id}`);
  });

  favorites.rpn_ids.forEach((id) => {
    favoritesSet.add(`rpn_${id}`);
  });

  favorites.fpn_ids.forEach((id) => {
    favoritesSet.add(`fpn_${id}`);
  });

  return favoritesSet;
});

/**
 * Check if a specific item is favorited
 * Usage: selectIsFavorited(state, 'product_line', 281)
 */
export const selectIsFavorited = createSelector(
  [selectFavoritesSet, (_state: RootState, type: string, id: number) => `${type}_${id}`],
  (favoritesSet, uniqueId) => {
    return favoritesSet.has(uniqueId);
  }
);
